// src/pages/HomePage.js
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProperties } from "../hooks/useProperties";
import PropertyCard from "../components/PropertyCard";
import Spinner from "../components/Spinner";
import { LOCATIONS, PROPERTY_TYPES } from "../utils/constants";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Sora:wght@400;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Plus Jakarta Sans', sans-serif; background: #f8faf8; color: #1a2e1a; }
  .hero { background: linear-gradient(135deg, #0f4d28 0%, #1a6b3c 55%, #22854b 100%); padding: 52px 16px 60px; position: relative; overflow: hidden; }
  .hero::before { content:''; position:absolute; inset:0; background:url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E"); }
  .search-box { background:white; border-radius:14px; padding:16px; box-shadow:0 8px 32px rgba(0,0,0,0.15); }
  .form-input { width:100%; padding:10px 14px; border:1.5px solid #e2ece2; border-radius:8px; font-family:inherit; font-size:14px; background:#fff; color:#1a2e1a; }
  .form-input:focus { outline:2px solid #1a6b3c; outline-offset:1px; }
  .btn-green { background:#1a6b3c; color:white; border:none; cursor:pointer; transition:background 0.2s; font-family:inherit; }
  .btn-green:hover { background:#22854b; }
  .tab-btn { padding:9px 22px; border-radius:24px; border:2px solid rgba(255,255,255,0.4); background:rgba(255,255,255,0.15); color:white; font-weight:700; font-size:14px; cursor:pointer; font-family:inherit; transition:all 0.15s; }
  .tab-btn.active { background:#f59e0b; border-color:#f59e0b; }
  .cat-btn { background:white; border:1px solid #e2ece2; border-radius:12px; padding:16px 10px; text-align:center; cursor:pointer; transition:transform 0.15s,box-shadow 0.15s; font-family:inherit; width:100%; }
  .cat-btn:hover { transform:translateY(-3px); box-shadow:0 8px 20px rgba(26,107,60,0.1); }
  @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  .fade-up { animation: fadeUp 0.4s ease both; }
`;

export default function HomePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("Buy");
  const [search, setSearch] = useState("");
  const [loc, setLoc] = useState("");
  const [type, setType] = useState("");

  const { properties: featured, loading } = useProperties({ pageSize: 4 });
  const { properties: urgent } = useProperties({ urgent: true, pageSize: 3 });

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (search) params.set("q", search);
    if (loc) params.set("location", loc);
    if (type) params.set("type", type);
    if (tab === "Rent") params.set("listing", "Rent");
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <>
      <style>{CSS}</style>

      {/* ── HERO ── */}
      <div className="hero">
        <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center", position: "relative" }}>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 700, letterSpacing: 1.2, marginBottom: 10, textTransform: "uppercase" }}>
            🏡 apnasatna.com — Satna's #1 Property Platform
          </p>
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,5vw,42px)", fontWeight: 700, color: "white", lineHeight: 1.2, marginBottom: 10 }}>
            Find Your Dream Property<br />
            <span style={{ color: "#f59e0b" }}>in Satna, MP</span>
          </h1>
          <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 15, marginBottom: 28 }}>
            Apna Ghar, Apni Zameen — Direct from Owners. No Broker Fees.
          </p>

          {/* Tabs */}
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 20 }}>
            {["Buy", "Rent", "Sell"].map(t => (
              <button key={t} className={`tab-btn${tab === t ? " active" : ""}`}
                onClick={() => t === "Sell" ? navigate("/add-property") : setTab(t)}>
                {t}
              </button>
            ))}
          </div>

          {/* Search box */}
          <div className="search-box">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
                placeholder="Search property, area, keyword..." className="form-input"
                style={{ gridColumn: "1/-1" }} />
              <select value={loc} onChange={e => setLoc(e.target.value)} className="form-input">
                <option value="">All Locations</option>
                {LOCATIONS.map(l => <option key={l}>{l}</option>)}
              </select>
              <select value={type} onChange={e => setType(e.target.value)} className="form-input">
                <option value="">All Types</option>
                {PROPERTY_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              <button onClick={handleSearch} className="btn-green"
                style={{ gridColumn: "1/-1", padding: 13, borderRadius: 9, fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                🔍 Search Properties
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div style={{ background: "#0f4d28", padding: "14px 16px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "center", gap: "clamp(20px,6vw,70px)", flexWrap: "wrap" }}>
          {[["500+","Properties"],["1200+","Happy Buyers"],["100%","Verified"],["Free","Listing"]].map(([v,l]) => (
            <div key={l} style={{ textAlign: "center" }}>
              <p style={{ color: "#f59e0b", fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 20 }}>{v}</p>
              <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 12 }}>{l}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "36px 16px" }}>

        {/* ── CATEGORIES ── */}
        <SectionHeader title="Browse by Category" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10, marginBottom: 40 }}>
          {[
            { emoji: "🏠", label: "Houses",    count: "120+", bg: "#e8f5e9", type: "House" },
            { emoji: "🌿", label: "Plots",     count: "80+",  bg: "#fff3e0", type: "Plot" },
            { emoji: "🏢", label: "Flats",     count: "200+", bg: "#e3f2fd", type: "Flat" },
            { emoji: "🏪", label: "Shops",     count: "60+",  bg: "#fce4ec", type: "Shop" },
            { emoji: "🏗️", label: "Commercial",count: "40+",  bg: "#f3e5f5", type: "Commercial" },
          ].map(c => (
            <button key={c.label} className="cat-btn" style={{ background: c.bg }}
              onClick={() => navigate(`/properties?type=${c.type}`)}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>{c.emoji}</div>
              <p style={{ fontWeight: 700, fontSize: 13, color: "#1a2e1a" }}>{c.label}</p>
              <p style={{ fontSize: 11, color: "#6b7c6b" }}>{c.count}</p>
            </button>
          ))}
        </div>

        {/* ── FEATURED ── */}
        <SectionHeader title="Featured Properties" link="/properties" linkLabel="View All →" navigate={navigate} />
        {loading ? <Spinner /> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 16, marginBottom: 40 }}>
            {featured.map(p => <div key={p.id} className="fade-up"><PropertyCard property={p} /></div>)}
          </div>
        )}

        {/* ── URGENT SALES ── */}
        {urgent.length > 0 && (
          <>
            <div style={{ background: "linear-gradient(135deg,#fde8e8,#fff5f5)", border: "1.5px solid #f5c6c6", borderRadius: 16, padding: "22px 18px", marginBottom: 40 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 24 }}>🔥</span>
                <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 18, color: "#c0392b" }}>Urgent Sale Properties</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14 }}>
                {urgent.map(p => <PropertyCard key={p.id} property={p} />)}
              </div>
            </div>
          </>
        )}

        {/* ── SELL CTA ── */}
        <div style={{ background: "#1a6b3c", borderRadius: 16, padding: "32px 24px", textAlign: "center", marginBottom: 40 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 22, color: "white", marginBottom: 8 }}>
            Apni Property Bechni Hai?
          </h2>
          <p style={{ color: "rgba(255,255,255,0.75)", marginBottom: 20, fontSize: 14 }}>
            ApnaSatna.com par FREE mein list karein — Satna ke hazaron buyers tak pahunchen.
          </p>
          <button onClick={() => navigate("/add-property")} style={{
            background: "#f59e0b", color: "white", border: "none", padding: "13px 30px",
            borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit"
          }}>
            + Post Property Free
          </button>
        </div>

        {/* ── HOW IT WORKS ── */}
        <SectionHeader title="How It Works" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 40 }}>
          {[
            { step: "1", emoji: "📝", title: "Post Your Listing", desc: "Fill a simple form with property details and photos." },
            { step: "2", emoji: "✅", title: "Get Verified", desc: "Our team verifies your listing within 24 hours." },
            { step: "3", emoji: "📞", title: "Buyers Contact You", desc: "Receive calls and WhatsApp enquiries directly." },
          ].map(s => (
            <div key={s.step} style={{ background: "white", border: "1px solid #e2ece2", borderRadius: 14, padding: 20, textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>{s.emoji}</div>
              <div style={{ width: 28, height: 28, background: "#1a6b3c", borderRadius: 14, color: "white", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>{s.step}</div>
              <p style={{ fontWeight: 700, fontSize: 15, color: "#1a2e1a", marginBottom: 5 }}>{s.title}</p>
              <p style={{ fontSize: 13, color: "#6b7c6b", lineHeight: 1.5 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ background: "#0f2a1a", color: "rgba(255,255,255,0.6)", textAlign: "center", padding: "24px 16px", fontSize: 13 }}>
        <p style={{ color: "white", fontWeight: 700, fontSize: 16, marginBottom: 4, fontFamily: "'Sora',sans-serif" }}>ApnaSatna.com</p>
        <p>Satna's most trusted real estate platform · Madhya Pradesh, India</p>
        <p style={{ marginTop: 8, fontSize: 11 }}>© 2025 ApnaSatna.com · All rights reserved.</p>
      </footer>
    </>
  );
}

function SectionHeader({ title, link, linkLabel, navigate }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 20, color: "#1a2e1a" }}>{title}</h2>
      {link && <button onClick={() => navigate(link)} style={{ background: "none", border: "none", color: "#1a6b3c", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>{linkLabel}</button>}
    </div>
  );
}
